// `Article` JSON-LD — Schema.org type for the editorial `/learn` and
// `/compare` pages. Article + BreadcrumbList are the reliable rich-result
// wins for long-form explainers (Google restricted standalone FAQ/HowTo
// rich results in 2023, but still reads `Article` for freshness, author,
// and the "in-depth article" treatment).
//
// Author + publisher reference the site-wide Organization node by `@id`
// instead of duplicating it — same pattern as `static-page.ts`. Dates are
// passed by the surface (build-time constants) so the payload is
// deterministic and cacheable.

import { organizationId } from './site';

import type { JsonLdNode, JsonLdPayload } from './types';

export interface BuildArticleOptions {
  /** Headline — usually the page H1. Google caps display at ~110 chars. */
  headline: string;
  /** 1-2 sentence summary; mirrors the meta description. */
  description: string;
  /** Canonical absolute URL of the page that owns the article. */
  pageUrl: string;
  /** Absolute URL of the page's OG image — becomes `image`. */
  imageUrl: string;
  /** ISO-8601 first-published date. */
  datePublished: string;
  /** ISO-8601 last-modified date — the freshness signal Google surfaces. */
  dateModified: string;
  /** Optional section label (e.g. "Opinion markets", "Comparisons"). */
  section?: string;
}

export function buildArticleJsonLd(opts: BuildArticleOptions): JsonLdPayload {
  const node: JsonLdNode = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${opts.pageUrl}#article`,
    headline: opts.headline,
    description: opts.description,
    image: opts.imageUrl,
    url: opts.pageUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': opts.pageUrl },
    datePublished: opts.datePublished,
    dateModified: opts.dateModified,
    author: { '@id': organizationId() },
    publisher: { '@id': organizationId() },
    ...(opts.section !== undefined ? { articleSection: opts.section } : {}),
  };
  return node;
}
